import React from 'react';

// Styles & Assets
import styles from './PollsFilter.module.css';
import { ReactComponent as FilterSVG } from '../../../assets/icons/filter-off-fill.svg';

// Services
import { getDaysFrequencyPoll, getFrequencies } from '../../../services/getPollAttribute';
import { getInquiryTypes } from '../../../services/Poll';

// Context
import FiltersContext from '../../../context/FiltersContext';

// Hooks
import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import RowButtonsFilter from '../../PatientsPage/RowButtonsFilter/RowButtonsFilter';
import CancelConfirmButtons from '../../UI/CancelConfirmButtons/CancelConfirmButtons';

// Interfaces
interface Options {
    id: number
    content: string
}
interface Props {
    defaultIDsEnvironment: number[]
    defaultIDsFrequencies: number[]
    defaultIDsViewTables: number[]
    defaultIDsType: number[]
    modalShow: React.Dispatch<React.SetStateAction<boolean>>
    setFilterBallActive: (isActive: boolean) => void
    handleConfirm: (IDsEnv: number[], IDsFreq: number[], IDsTable: number[], IDsType: number[]) => void
}


const PollsFilter: React.FC<Props> = ({
    defaultIDsEnvironment,
    defaultIDsFrequencies,
    defaultIDsViewTables,
    defaultIDsType,
    setFilterBallActive,
    modalShow,
    handleConfirm
}) => {

    const { t } = useTranslation();

    const { profiles } = useContext(FiltersContext);

    const environmentOptions: Options[] = profiles.map(p => ({
        id: p.id,
        content: p.name || '',
    }));

    const frequenciesOptions: Options[] = getFrequencies().map(freq => ({ id: getDaysFrequencyPoll(freq), content: t(freq) }));
    const viewInTablesOptions: Options[] = [{ id: 0, content: t('yes') }, { id: 1, content: t('no') }];

    const inquiryTypesOptions: Options[] = getInquiryTypes();

    const [selectedIDsEnvironment, setSelectedIDsEnvironment] = useState<number[]>(defaultIDsEnvironment);
    const [selectedIDsFrequencies, setSelectedIDsFrequencies] = useState<number[]>(defaultIDsFrequencies);
    const [selectedIDsViewTables, setSelectedIDsViewTables] = useState<number[]>(defaultIDsViewTables);
    const [selectedTypeIDs, setSelectedTypeIDs] = useState<number[]>(defaultIDsType);

    const handleClickFilterEnvironment = (id: number) => {
        setSelectedIDsEnvironment(oldFilters => {
            const newFilters = [...oldFilters];
            return (newFilters.includes(id)) ?
                newFilters.filter(oldID => oldID !== id)
                : [...newFilters, id];
        });
    };

    const onConfirm = () => {
        setFilterBallActive(selectedIDsEnvironment.length !== 0 || selectedIDsFrequencies.length !== 0 || selectedIDsViewTables.length !== 0);
        handleConfirm(selectedIDsEnvironment, selectedIDsFrequencies, selectedIDsViewTables, selectedTypeIDs);
        modalShow(false);
    };

    const deleteFilters = () => {
        setSelectedIDsEnvironment([]);
        setSelectedIDsFrequencies([]);
        setSelectedIDsViewTables([]);
        setSelectedTypeIDs([]);
    };

    return (
        <div className='dialog'>
            <div className={styles.topOptions}>
                <div>
                    <h1 className='dialogTitle'>{t('filter')}</h1>
                    <h2 className='dialogSubTitle'>{t('polls')}</h2>
                </div>
                <button onClick={deleteFilters} className='buttonRemoveFilter'>
                    {t('remove_filters')} <FilterSVG />
                    </button>
            </div>
            <div className={styles.filtersContent}>
                <RowButtonsFilter
                    text={t('by_area')}
                    currentFilters={selectedIDsEnvironment}
                    handleClick={handleClickFilterEnvironment}
                    content={environmentOptions}
                    gridDisplay={environmentOptions.length > 4 || undefined}
                />
                <RowButtonsFilter
                    text={t('by_frequency')}
                    currentFilters={selectedIDsFrequencies}
                    setFilters={setSelectedIDsFrequencies}
                    content={frequenciesOptions}
                    gridDisplay
                />
                <div className={styles.gridRow}>
                    <RowButtonsFilter
                        text={t('view_in_tables')}
                        currentFilters={selectedIDsViewTables}
                        setFilters={setSelectedIDsViewTables}
                        content={viewInTablesOptions}
                    />
                    {window.config.DIFFERENCE_TESTPROM_POLLS &&
                        <RowButtonsFilter
                            text={t('type_poll')}
                            currentFilters={selectedTypeIDs}
                            setFilters={setSelectedTypeIDs}
                            content={inquiryTypesOptions}
                        />
                    }
                </div>
            </div>
            <CancelConfirmButtons
                onCancel={() => modalShow(false)}
                onConfirm={onConfirm}
            />
        </div>
    );
};

export default PollsFilter;